/* eslint-disable no-useless-return */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridApi, SideBarDef } from "ag-grid-community";
import { useFormikContext } from "formik";
import { toast } from "react-toastify";
import { Dialog } from "@mui/material";
import { Button, ChevronDown, Filter, Search, X } from "./utils/ComponentUtils";
import {
  Box,
  ButtonCancelSecondary,
  ButtonPrimary,
  ButtonPrimary1,
  getGrantStrikePrice,
  HStack,
  VStack,
} from "../../../components/utils";
import { Input } from "../../../components/shared/InputField";
import { Popover, PopoverContent, PopoverTrigger } from "./utils/Popover";
import { Select } from "../../../components/shared/Select";
import {
  EmployeeOfferDetails,
  GrantOfferDetails,
  NameAndId,
  TransactionDetailsData,
} from "../../../types/Transaction";
import { ChvronDown } from "../../../utils/assets/chevronDown";
import { formatDisplayDate } from "../../../utils/date";
import SearchInput from "../../../components/shared/SearchInput";
import Avatar from "../../esopOverview/Avatar";
import FilterOptions from "./utils/FilterOptions";
import {
  formatCurrency,
  getCurrencySymbol,
  getCurrencyType,
} from "../../../utils/currencyFormatter";
import { OfferState } from "../../../types/OptionBuyback";
import TransactionStatusLabel from "./utils/TransactionStatusLabel";
import { Action, CTADropdown } from "../../../components/shared/Dropdown";
import {
  getEmployeeBlackout,
  SummaryTransactionCard,
} from "./TransactionStartedDetails";
import DocumentsUploaded from "./utils/DocumentsUploaded";
import {
  SummaryCardType,
  SummaryTransactionCards,
} from "./utils/SummaryTransactionCards";
import { ConversionMethodology } from "../../../types/Exercise";

interface ExerciseSummaryProps {
  onStepChange: (e?: number) => void;
  onBackClick?: () => void;
}

interface FilterType {
  id: string;
  label: string;
  options?: string[];
  type: string;
}

interface Employee {
  id: number;
  name: string;
  position: string;
  hrId: string;
  vestedOptions: number;
  eligibility: number;
  willingness: string;
  status: string;
  avatar: string;
}

interface FilterState {
  department: string[];
  vestedOptions: {
    min: number;
    max?: number;
  };
  eligibleOptions: {
    min: number;
    max?: number;
  };
  employmentType: string[];
  tenure: {
    min: number;
    max?: number;
  };
  participation: string;
}
const ExerciseSummary = (props: ExerciseSummaryProps) => {
  // Filter states
  const formik = useFormikContext<TransactionDetailsData>();

  const currencySymbol = getCurrencySymbol();
  const currencyType = getCurrencyType();

  const [attachDocumentsDialog, setAttachDocumentsDialog] = useState<{
    open: boolean;
    employeeId?: string;
  }>({ open: false, employeeId: undefined });

  const handleAction = (employee: EmployeeOfferDetails, action: Action) => {
    if (action.disabled) {
      return;
    }

    if (action.name === "Attach Documents") {
      setAttachDocumentsDialog({
        open: true,
        employeeId: employee.employeeId,
      });
      return;
    }
  };

  const gridApi = useRef<GridApi | any>(null);
  const onGridReady = (params: any) => {
    gridApi.current = params;
  };

  const rowData = useMemo(
    () =>
      formik.values.selectedEmployeesList?.map((employee) => ({
        ...employee,
        sharesIssued:
          employee.offerState === OfferState.COMPLETED
            ? employee.grantRelatedDetails?.reduce(
                (sum: number, grant: GrantOfferDetails) =>
                  sum +
                  (grant.conversionMethodology ===
                  ConversionMethodology.ROUND_DOWN
                    ? Math.floor(
                        (grant.willingness || 0) * (grant.conversionRatio || 1)
                      )
                    : Math.ceil(
                        (grant.willingness || 0) * (grant.conversionRatio || 1)
                      )),
                0
              ) || 0
            : 0,
        actions: (
          <CTADropdown
            dropdownClassName=" xl:right-10 lg:right-8 md:right-2"
            actions={[
              {
                name: "Attach Documents",
                tooltipText: "",
              },
            ]}
            onAction={(action) => handleAction(employee, action)}
          />
        ),
      })) || [],
    [formik.values.selectedEmployeesList]
  );

  const isValidDate = (date: string) =>
    Number.isNaN(date) &&
    new Date(date).toString() !== "Invalid Date" &&
    !Number.isNaN(Date.parse(date));

  const normaliseValue = (value: string | number) => {
    if (typeof value === "number") return value;
    if (typeof value === "string") {
      if (isValidDate(value)) return new Date(value).valueOf();
      try {
        value = value.replaceAll(",", "");
      } catch (TypeError) {
        value = value.replace(/,./g, "");
      }
      return parseFloat(value);
    }
    return value;
  };

  const sortComparator = (
    valueA: number | string,
    valueB: number | string,
    ..._: any[]
  ) => normaliseValue(valueA) - normaliseValue(valueB);

  const sideBar = useMemo<SideBarDef>(
    () => ({
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          minWidth: 225,
          width: 225,
          maxWidth: 225,
          toolPanelParams: {
            suppressRowGroups: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressValues: true,
          },
        },
        {
          id: "filters",
          labelDefault: "Filters",
          labelKey: "filters",
          iconKey: "filter",
          toolPanel: "agFiltersToolPanel",
          minWidth: 180,
          maxWidth: 400,
          width: 250,
        },
      ],
    }),
    []
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      wrapText: true,
      flex: 1,
      autoHeight: true,
      initialWidth: 150,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      cellClass: "multiline",
      minWidth: 150,
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      comparator: sortComparator,
      filterParams: {
        buttons: ["reset"],
        maxNumConditions: 5,
      },
      filter: true,
      resizable: true,
    }),
    []
  );

  function EmployeeInfoRender(emp: any) {
    return (
      <HStack>
        <Avatar
          name={emp.data.employeeName}
          status={emp.data.employmentStatus}
        />
        <Box className="ml-2">
          <p className="text-xs font-medium text-gray-dark">
            {emp.data.employeeName}
          </p>
          <p className="capitalize text-xxs text-gray-light">
            {emp.data.employeeDepartment || ""} |{" "}
            {emp.data.employeeDesignation || ""}
          </p>
        </Box>
      </HStack>
    );
  }

  function PreviewListStatus(status: any) {
    return (
      <HStack>
        <TransactionStatusLabel state={status} />
      </HStack>
    );
  }

  function RequestedCellRender(params: any) {
    return (
      <VStack className="items-center justify-center gap-1">
        <p
          className={`text-xs  text-gray-dark ${
            params.data?.isTotalRow ? "font-semibold" : "font-medium"
          }`}
        >
          {(params.totalWillingness || 0).toLocaleString(currencyType)}
        </p>
        {!params.data?.isTotalRow && (
          <p className="text-xs font-medium text-gray-400">
            {parseFloat(
              (
                (params.totalWillingness / params.totalEligibleOptions) *
                100
              ).toFixed(4)
            )}
            %
          </p>
        )}
      </VStack>
    );
  }
  const exercisePricePaid: ColDef | undefined = formik.values.monetaryDetails
    .transactionPaymentEnabled
    ? {
        headerName: "EXERCISE PRICE",
        autoHeight: true,
        colId: "exercisePricePaid",
        valueGetter: (params: any) => ({
          amount:
            params.data?.transactionCompensationDetails?.totalStrikePrice || 0,
          paymentStatus:
            params.data?.paymentDetails?.find(
              (p: any) => p.paymentType.toLowerCase() === "exercise price"
            )?.paymentStatus || null,
        }),
        filter: "agMultiColumnFilter",
        menuTabs: ["filterMenuTab"],
        filterParams: {
          maxNumConditions: 5,
          buttons: ["reset"],
          filters: [
            {
              title: "Amount",
              display: "subMenu",
              filter: "agNumberColumnFilter",
              filterParams: {
                buttons: ["reset"],
                valueGetter: (params: any) =>
                  params.data?.transactionCompensationDetails
                    ?.totalStrikePrice || 0,
              },
            },
            {
              title: "Payment Status",
              display: "subMenu",
              filter: "agSetColumnFilter",
              filterParams: {
                buttons: ["reset"],
                keyCreator: (params: any) => {
                  const s = params.value?.paymentStatus;
                  if (s === "SUCCESS") return "PAID";
                  if (s === "FAILED") return "FAILED";
                  if (s === "PAYMENT_PENDING") return "PENDING APPROVAL";
                  return "NOT SUBMITTED";
                },
                valueFormatter: (params: any) => {
                  const s = params.value?.paymentStatus;
                  if (s === "SUCCESS") return "PAID";
                  if (s === "FAILED") return "FAILED";
                  if (s === "PAYMENT_PENDING") return "PENDING APPROVAL";
                  return "NOT SUBMITTED";
                },
              },
            },
          ],
        },
        flex: 1,
        width: 150,
        cellStyle: { paddingTop: "15px", lineHeight: "20px" },
        cellRenderer: (params: any) => {
          if (params.data?.isTotalRow) {
            return null;
          }
          const paymentDetails = params.data.paymentDetails || [];
          const latestExercisePrice = paymentDetails.find(
            (p: any) => p.paymentType.toLowerCase() === "exercise price"
          );
          const transactionCompensationDetails =
            params.data.transactionCompensationDetails;
          return (
            <VStack className="items-center justify-between gap-1">
              <p className="text-xs font-medium text-gray-dark">
                {formatCurrency(
                  transactionCompensationDetails?.totalStrikePrice || 0,
                  currencyType
                )}
              </p>
              <p
                className={`text-xs font-medium ${
                  latestExercisePrice?.paymentStatus === "FAILED"
                    ? "text-red-500"
                    : latestExercisePrice?.paymentStatus === "SUCCESS"
                    ? "text-[#069F10]"
                    : latestExercisePrice?.paymentStatus === "PAYMENT_PENDING"
                    ? "text-[#E29500]"
                    : "text-gray-400"
                }`}
              >
                {latestExercisePrice?.paymentStatus === "SUCCESS"
                  ? "PAID"
                  : latestExercisePrice?.paymentStatus === "FAILED"
                  ? "FAILED"
                  : latestExercisePrice?.paymentStatus === "PAYMENT_PENDING"
                  ? "PENDING APPROVAL"
                  : "NOT SUBMITTED"}
              </p>
            </VStack>
          );
        },
      }
    : undefined;

  const taxPaid: ColDef | undefined = formik.values.monetaryDetails
    .transactionPaymentEnabled
    ? {
        headerName: "TAX PRICE",
        autoHeight: true,
        colId: "taxPricePaid",
        valueGetter: (params: any) => ({
          amount:
            params.data?.transactionCompensationDetails?.totalTaxAmount || 0,
          paymentStatus:
            params.data?.paymentDetails?.find(
              (p: any) => p.paymentType.toLowerCase() === "tax price"
            )?.paymentStatus || null,
        }),
        filter: "agMultiColumnFilter",
        menuTabs: ["filterMenuTab"],
        filterParams: {
          maxNumConditions: 5,
          buttons: ["reset"],
          filters: [
            {
              title: "Amount",
              display: "subMenu",
              filter: "agNumberColumnFilter",
              filterParams: {
                buttons: ["reset"],
                valueGetter: (params: any) =>
                  params.data?.transactionCompensationDetails?.totalTaxAmount ||
                  0,
              },
            },
            {
              title: "Payment Status",
              display: "subMenu",
              filter: "agSetColumnFilter",
              filterParams: {
                buttons: ["reset"],
                keyCreator: (params: any) => {
                  const s = params.value?.paymentStatus;
                  if (s === "SUCCESS") return "PAID";
                  if (s === "FAILED") return "FAILED";
                  if (s === "PAYMENT_PENDING") return "PENDING APPROVAL";
                  return "NOT SUBMITTED";
                },
                valueFormatter: (params: any) => {
                  const s = params.value?.paymentStatus;
                  if (s === "SUCCESS") return "PAID";
                  if (s === "FAILED") return "FAILED";
                  if (s === "PAYMENT_PENDING") return "PENDING APPROVAL";
                  return "NOT SUBMITTED";
                },
              },
            },
          ],
        },
        flex: 1,
        width: 150,
        cellStyle: { paddingTop: "15px", lineHeight: "20px" },
        cellRenderer: (params: any) => {
          if (params.data?.isTotalRow) {
            return null;
          }
          const paymentDetails = params.data.paymentDetails || [];
          const latestTaxPrice = paymentDetails.find(
            (p: any) => p.paymentType.toLowerCase() === "tax price"
          );
          const transactionCompensationDetails =
            params.data.transactionCompensationDetails;
          return (
            <VStack className="items-center justify-between gap-1">
              <p className="text-xs font-medium text-gray-dark">
                {formatCurrency(
                  transactionCompensationDetails?.totalTaxAmount || 0,
                  currencyType
                )}
              </p>
              <p
                className={`text-xs font-medium ${
                  latestTaxPrice?.paymentStatus === "FAILED"
                    ? "text-red-500"
                    : latestTaxPrice?.paymentStatus === "SUCCESS"
                    ? "text-[#069F10]"
                    : latestTaxPrice?.paymentStatus === "PAYMENT_PENDING"
                    ? "text-[#E29500]"
                    : "text-gray-400"
                }`}
              >
                {latestTaxPrice?.paymentStatus === "SUCCESS"
                  ? "PAID"
                  : latestTaxPrice?.paymentStatus === "FAILED"
                  ? "FAILED"
                  : latestTaxPrice?.paymentStatus === "PAYMENT_PENDING"
                  ? "PENDING APPROVAL"
                  : "NOT SUBMITTED"}
              </p>
            </VStack>
          );
        },
      }
    : undefined;

  const columnDefs: ColDef[] = [
    {
      headerName: "EMPLOYEE NAME",
      width: 200,
      checkboxSelection: true, // <-- Add this
      headerCheckboxSelection: true,
      autoHeight: true,
      cellStyle: { paddingTop: "15px", lineHeight: "20px" },
      wrapText: true,
      colId: "employeeName",
      valueGetter: (params: any) => ({
        employeeName: params.data?.employeeName,
        employeeDepartment: params.data?.employeeDepartment,
        employeeDesignation: params.data?.employeeDesignation,
        hrId: params.data?.hrId,
      }),
      filter: "agMultiColumnFilter",
      menuTabs: ["filterMenuTab"],
      filterParams: {
        maxNumConditions: 5,
        buttons: ["reset"],
        filters: [
          {
            title: "Name",
            display: "subMenu",
            filter: "agSetColumnFilter",
            filterParams: {
              buttons: ["reset"],
              keyCreator: (params: any) => params.value?.employeeName,
              valueFormatter: (params: any) => params.value?.employeeName,
            },
          },
          {
            title: "Department",
            display: "subMenu",
            filter: "agSetColumnFilter",
            filterParams: {
              buttons: ["reset"],
              keyCreator: (params: any) => params.value?.employeeDepartment,
              valueFormatter: (params: any) => params.value?.employeeDepartment,
            },
          },
          {
            title: "Designation",
            display: "subMenu",
            filter: "agSetColumnFilter",
            filterParams: {
              buttons: ["reset"],
              keyCreator: (params: any) => params.value?.employeeDesignation,
              valueFormatter: (params: any) =>
                params.value?.employeeDesignation,
            },
          },
          {
            title: "Emp ID",
            display: "subMenu",
            filter: "agSetColumnFilter",
            filterParams: {
              buttons: ["reset"],
              keyCreator: (params: any) => params.value?.hrId,
              valueFormatter: (params: any) => params.value?.hrId,
            },
          },
        ],
      },
      comparator(valueA, valueB) {
        return valueA?.employeeName
          ?.toLowerCase()
          ?.localeCompare(valueB?.employeeName?.toLowerCase());
      },
      initialWidth: 200,
      minWidth: 200,
      cellRendererParams: (params: any) => ({
        employeeName: params.data.employeeName,
        employeeDesignation: params.data.employeeDesignation,
        employeeDepartment: params.data.employeeDepartment,
        employeeState: params.data.employeeState,
      }),
      cellRenderer: (params: any) =>
        params.data?.isTotalRow ? "Total" : EmployeeInfoRender(params),
      getQuickFilterText: (params: any) =>
        [
          params.data.employeeName,
          params.data.employeeDesignation,
          params.data.employeeDepartment,
          params.data.employeeState,
          params.data.hrId,
        ]
          .filter(Boolean)
          .join(" "),
    },
    {
      headerName: "WILLINGNESS",
      autoHeight: true,
      field: "totalWillingness",
      filter: "agNumberColumnFilter",
      menuTabs: ["filterMenuTab"],
      flex: 1,
      width: 150,
      cellStyle: { paddingTop: "15px", lineHeight: "20px" },

      cellRendererParams: (params: any) => ({
        totalWillingness: params.data.totalWillingness,
        totalEligibleOptions: (() => {
          const percentage =
            formik.values.monetaryDetails.percentageOfNetVestedOptions || 0;
          const totalEligibleOptions = params.data.totalEligibleOptions || 0;

          return percentage > 0
            ? Math.floor(totalEligibleOptions * (percentage / 100))
            : totalEligibleOptions;
        })(),
      }),
      cellRenderer: (params: any) => RequestedCellRender(params),
    },
    ...(exercisePricePaid ? [exercisePricePaid] : []),
    ...(taxPaid ? [taxPaid] : []),
    {
      headerName: "SHARES ISSUED",
      autoHeight: true,
      field: "sharesIssued",
      filter: "agNumberColumnFilter",
      menuTabs: ["filterMenuTab"],
      flex: 1,
      width: 120,
      cellStyle: { paddingTop: "15px", lineHeight: "20px" },
      valueFormatter: (params) =>
        (params.value || 0).toLocaleString(currencyType),
      cellRenderer: (params: any) => {
        const sharesIssued = params.data.sharesIssued || 0;
        return (
          <VStack className="items-center justify-between gap-1">
            <p className="text-xs font-medium text-gray-dark">
              {(sharesIssued || 0).toLocaleString(currencyType)}
            </p>
          </VStack>
        );
      },
    },
    {
      headerName: "STATUS",
      autoHeight: true,
      field: "offerState",
      filter: "agSetColumnFilter",
      menuTabs: ["filterMenuTab"],
      flex: 1,
      width: 80,
      cellStyle: { paddingTop: "15px", lineHeight: "20px" },
      cellRendererParams: (params: any) => ({
        params,
      }),
      cellRenderer: (params: any) => PreviewListStatus(params.value),
    },
    {
      headerName: "",
      field: "actions",
      pinned: "right",
      hide: false,
      width: 80,
      maxWidth: 80,
      filter: false,
      colId: "action-column",
      suppressNavigable: true,
      suppressColumnsToolPanel: true,
      resizable: false,
      sortable: false,
      autoHeight: true,
      menuTabs: ["columnsMenuTab"],
      cellRendererParams: ({ value }: { value: { props: any } }) =>
        value ? value.props : null,
      cellRenderer: CTADropdown,
    },
  ];

  const handleBackClick = () => {
    if (props.onBackClick) {
      props.onBackClick();
    }
  };

  function setPinnedBottomRowData({ api }: { api: any }) {
    const data = api.rowModel.rootNode;
    const filteredData = data.childrenAfterAggFilter.map(
      (element: any) => element.data
    );

    const totalVestedOptions = filteredData?.reduce(
      (accumulator: any, data: any) => accumulator + data.totalVestedOptions,
      0
    );
    const totalEligibility = filteredData?.reduce(
      (accumulator: any, data: any) => accumulator + data.totalEligibleOptions,
      0
    );
    const totalWillingness = filteredData?.reduce(
      (accumulator: any, data: any) => accumulator + data.totalWillingness,
      0
    );
    const totalSharesIssued = filteredData?.reduce(
      (accumulator: any, data: any) => accumulator + data.sharesIssued,
      0
    );

    api.setPinnedBottomRowData([
      {
        employeeName: "Total",
        totalVestedOptions,
        totalEligibleOptions: totalEligibility,
        totalWillingness,
        sharesIssued: totalSharesIssued,
        isTotalRow: true,
      },
    ]);
  }

  return (
    <VStack className="w-full">
      <HStack className="justify-between gap-1 pb-3 px-6 font-medium border-b-[0.5px] pt-7">
        <VStack>
          <HStack className="flex justify-between text-xl font-semibold text-black-501">
            Summary
          </HStack>
          <HStack className="flex text-sm font-medium text-gray-500">
            This screen provides a final snapshot of the exercise event. All key
            actions have been completed. Use this view to verify that the event
            is ready to be marked as complete. No further changes can be made
            after completion.
          </HStack>
        </VStack>
        <VStack className="items-end">
          <HStack className="flex justify-between text-sm font-semibold text-black-501">
            {formatDisplayDate(formik.values.endDate)}
          </HStack>
          <HStack className="flex text-xs font-medium text-gray-500 whitespace-nowrap">
            Window Closed
          </HStack>
        </VStack>
      </HStack>
      <Dialog
        open={attachDocumentsDialog.open}
        onClose={() =>
          setAttachDocumentsDialog({ open: false, employeeId: undefined })
        }
        maxWidth="lg"
      >
        <DocumentsUploaded
          employeeId={attachDocumentsDialog.employeeId || ""}
          transactionId={formik.values.id || ""}
          onClose={() =>
            setAttachDocumentsDialog({ open: false, employeeId: undefined })
          }
        />
      </Dialog>
      <div className="px-6 py-4">
        <HStack className="justify-between gap-1 pb-3 px-6 font-medium border-b-[0.5px] pt-7 shadow-custom">
          <VStack className="w-full">
            <VStack className="pb-2 border-b">
              <HStack className="flex justify-between text-xl font-semibold text-black-501">
                Exercise Q2 2024
              </HStack>
              <HStack className="flex text-sm font-medium text-gray-500 ">
                {`${formatDisplayDate(
                  formik.values.startDate
                )} - ${formatDisplayDate(formik.values.endDate)} `}
              </HStack>
            </VStack>
            <HStack className="justify-between gap-16 px-6 py-8 text-center">
              <VStack className="gap-6">
                <p>FMV</p>
                <p>
                  {formik.values.monetaryDetails.isLatestFMV
                    ? "Dynamic"
                    : formatCurrency(
                        formik.values.monetaryDetails.fmvPrice,
                        currencyType
                      )}
                </p>
              </VStack>
              <VStack className="gap-6">
                <p>Reoccurrence</p>
                <p>
                  {formik.values.recurringSetting.isRecurring ? "Yes" : "No"}
                </p>
              </VStack>
              <VStack className="gap-6">
                <p>Participant List</p>
                <p>{formik.values.monetaryDetails.participantListMethod}</p>
              </VStack>
            </HStack>
          </VStack>
        </HStack>
      </div>
      <div className="w-full px-6 pb-8">
        <VStack className="px-6 py-4 ">
          <HStack className="flex gap-4 pt-6 text-base font-semibold text-center text-black-501">
            <SummaryTransactionCards
              cardTypes={
                [
                  "employees",
                  "optionsExercised",
                  "exercisePricePaid",
                  "taxPaid",
                ] as SummaryCardType[]
              }
              currencyType={currencyType}
            />
          </HStack>
        </VStack>
      </div>
      <div className="w-full px-6 pb-8">
        {formik.values.monetaryDetails.participantListMethod ===
          "Generate By Filters" && (
          <HStack className="w-full gap-4 px-6 py-4 border border-black rounded-md ">
            {
              <div className="flex flex-wrap gap-4">
                <FilterOptions formik={formik} disableUpdate={true} />
              </div>
            }
          </HStack>
        )}
      </div>

      <HStack className="justify-end gap-8 px-6 pt-4">
        <div className="flex gap-2 text-xs text-center justify-evenly">
          <SearchInput
            className=""
            placeholder={`Search`}
            onChange={(e: any) => {
              gridApi.current.api.setQuickFilter(e.target.value);
            }}
          />
        </div>
      </HStack>
      {/* AG Grid table */}
      <HStack className="justify-between w-full pt-4">
        <Box
          style={{
            height: `${
              rowData.length <= 3
                ? "300"
                : rowData.length >= 10
                ? "600"
                : rowData.length * 100
            }px`,
          }}
          className="w-full h-full max-h-full overflow-x-auto bg-black ag-theme-material"
        >
          <AgGridReact
            getRowStyle={(params) => {
              if (params.data?.isTotalRow) {
                return {
                  fontWeight: 600,
                };
              }
              return undefined;
            }}
            onRowDataUpdated={setPinnedBottomRowData}
            rowData={rowData}
            rowSelection="multiple"
            columnDefs={columnDefs}
            onGridReady={onGridReady}
            suppressRowTransform={true}
            sideBar={sideBar}
            defaultColDef={defaultColDef}
            animateRows={true}
            suppressRowClickSelection
            suppressMenuHide={true}
            pagination={true}
            rowClass={
              "border-t border-dashed cursor-pointer hover:bg-slate-50 "
            }
            overlayNoRowsTemplate={
              '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow;   margin-top: 50px;">No Rows To Show</span>'
            }
          ></AgGridReact>
        </Box>
      </HStack>

      <HStack className="justify-between w-full p-6">
        <ButtonPrimary1 onClick={handleBackClick}>Back</ButtonPrimary1>
      </HStack>
    </VStack>
  );
};

export default ExerciseSummary;
